import makeObservable from "./makeObservable";

const DEFAULT_SEARCH_RESULT_LIMIT = 50;

const INITIAL_SEARCH_RESULT_NAVIGATION_STATE = {
  currentSearchParams: {},
  count: 0,
  limit: DEFAULT_SEARCH_RESULT_LIMIT,
  listContext: {},
  offset: 0,
  pageIds: [],
  statusContext: "all",
};

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getPageIds = (edges = []) =>
  edges
    .map(edge => {
      const rawId = edge?.id;
      if (rawId === null || rawId === undefined || rawId === "") {
        return NaN;
      }
      return toNumber(rawId, NaN);
    })
    .filter(Number.isFinite);

const buildSearchResultNavigationSnapshot = ({
  count = 0,
  edges = [],
  listContext = {},
  queryVariables = {},
  statusContext = "all",
} = {}) => {
  const limit =
    toNumber(queryVariables?.limit, DEFAULT_SEARCH_RESULT_LIMIT) ||
    DEFAULT_SEARCH_RESULT_LIMIT;
  const offset = toNumber(queryVariables?.offset, 0);
  const currentSearchParams = {
    ...queryVariables,
    limit,
  };

  delete currentSearchParams.offset;

  return {
    currentSearchParams,
    count: toNumber(count, 0),
    limit,
    listContext,
    offset,
    pageIds: getPageIds(edges),
    statusContext,
  };
};

const getSearchResultNavigationState = ({ recordId, snapshot } = {}) => {
  const emptyState = {
    hasNext: false,
    hasPrevious: false,
    nextId: null,
    nextOffset: null,
    previousId: null,
    previousOffset: null,
  };

  if (!snapshot || typeof recordId !== "number") {
    return emptyState;
  }

  const {
    count = 0,
    limit = DEFAULT_SEARCH_RESULT_LIMIT,
    offset = 0,
    pageIds = [],
  } = snapshot;
  const indexOnPage = pageIds.findIndex(id => id === recordId);

  if (indexOnPage < 0) {
    return emptyState;
  }

  const globalIndex = offset + indexOnPage;
  const hasPrevious = globalIndex > 0;
  const hasNext = globalIndex < count - 1;
  const previousId = indexOnPage > 0 ? pageIds[indexOnPage - 1] : null;
  const nextId =
    indexOnPage < pageIds.length - 1 ? pageIds[indexOnPage + 1] : null;

  return {
    hasNext,
    hasPrevious,
    nextId,
    nextOffset: hasNext && !nextId ? offset + limit : null,
    previousId,
    previousOffset:
      hasPrevious && !previousId ? Math.max(offset - limit, 0) : null,
  };
};

const createSearchResultNavigationStore = () =>
  makeObservable(INITIAL_SEARCH_RESULT_NAVIGATION_STATE);

const hasSearchResultNavigationContext = ({ snapshot, status } = {}) =>
  snapshot?.statusContext === status && (snapshot?.pageIds || []).length > 0;

const updateSearchResultNavigationStore = (store, values) => {
  store.set(buildSearchResultNavigationSnapshot(values));
};

export {
  DEFAULT_SEARCH_RESULT_LIMIT,
  INITIAL_SEARCH_RESULT_NAVIGATION_STATE,
  buildSearchResultNavigationSnapshot,
  createSearchResultNavigationStore,
  getSearchResultNavigationState,
  hasSearchResultNavigationContext,
  updateSearchResultNavigationStore,
};
